import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  Receipt,
  Target,
  LogOut,
  Building2,
  Lock,
  Menu,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const LOGO_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_vendor-analytics-31/artifacts/zyxdgy8e_WhatsApp%20Image%202026-09-23%20at%2014.31.46.jpeg";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/", enabled: true },
  { label: "Vendas", icon: Receipt, path: "/vendas", enabled: true },
  { label: "Vendedores", icon: Users, path: "/vendedores", enabled: true },
  { label: "Metas", icon: Target, path: "/metas", enabled: true },
];

function SidebarContent({ onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border px-6 py-5">
        <img
          src={LOGO_URL}
          alt="VoughTech"
          className="h-9 w-9 rounded-full object-cover ring-1 ring-primary/25"
        />
        <span className="font-heading text-lg font-extrabold tracking-tight">
          VoughTech
        </span>
      </div>
      <nav className="flex flex-col gap-1 p-4">
        {NAV.map(({ label, icon: Icon, path, enabled }) => {
          const active = pathname === path;
          return (
            <button
              key={label}
              data-testid={`nav-${label.toLowerCase()}`}
              disabled={!enabled}
              onClick={() => {
                if (!enabled) return;
                navigate(path);
                onNavigate?.();
              }}
              className={cn(
                "flex items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors",
                active
                  ? "bg-primary/10 font-medium text-primary"
                  : enabled
                  ? "text-muted-foreground hover:bg-accent hover:text-foreground"
                  : "cursor-not-allowed text-muted-foreground/50"
              )}
            >
              <span className="flex items-center gap-3">
                <Icon className="h-4 w-4" />
                {label}
              </span>
              {!enabled && <Lock className="h-3.5 w-3.5" />}
            </button>
          );
        })}
      </nav>
      <div className="mt-auto px-6 pb-4 text-xs text-muted-foreground">
        Voughtech · MVP Web
      </div>
    </div>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card md:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-64 border-r border-border bg-card">
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <Button
              data-testid="mobile-menu-button"
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 text-secondary" />
              <span data-testid="company-name" className="font-medium text-foreground">
                {user?.company?.name || "—"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="hidden text-right sm:block">
              <p data-testid="user-name" className="text-sm font-medium text-foreground">
                {user?.name}
              </p>
              <p data-testid="user-email" className="text-xs text-muted-foreground">
                {user?.email}
              </p>
            </div>
            <Button
              data-testid="logout-button"
              variant="outline"
              size="sm"
              onClick={logout}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
